import { CloseRounded as CloseIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { readableDate } from "common/dates";
import CodeEditor, { DiffEditor } from "components/admin/CodeEditor";
import UserDetail from "components/admin/UserDetail";
import CustomButton from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import IconButton from "components/common/IconButton";
import { useState, useEffect, useCallback } from "react";
import {
  useListStaticDataVersionsQuery,
  useGetStaticDataVersionQuery,
} from "services/adminService";

const EDITOR_HEIGHT = "calc(90vh - 200px)";

const asUtcIso = (iso) =>
  /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;

const AuditLogTooltip = ({ log, children }) => (
  <Tooltip
    arrow
    placement={"right"}
    title={
      log ? (
        <Stack alignItems={"flex-start"} gap={1}>
          <UserDetail email={log.email} compact />
          <Stack>
            {log.userMessage && (
              <Typography variant={"body2"}>{log.userMessage}</Typography>
            )}
            <Typography variant={"caption"} sx={{ opacity: 0.7 }}>
              {readableDate(asUtcIso(log.date), { includeTime: true })}
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <Typography variant={"caption"}>{"No log found"}</Typography>
      )
    }
  >
    {children}
  </Tooltip>
);

const ConfigHistoryModal = ({
  open,
  close,
  companyId,
  staticDataKey,
  title,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);

  const {
    currentData: versions,
    isLoading: versionsLoading,
    isError: versionsError,
    refetch: refetchVersions,
  } = useListStaticDataVersionsQuery(
    { companyId, key: staticDataKey },
    { skip: !open || !companyId || !staticDataKey }
  );

  useEffect(() => {
    if (open) setSelectedIds([]);
  }, [open, staticDataKey]);

  useEffect(() => {
    if (versions?.length > 0) {
      setSelectedIds([versions[0].versionId]);
    }
  }, [versions]);

  const toggleVersion = useCallback(
    (versionId) => {
      setSelectedIds((prev) => {
        if (prev.includes(versionId))
          return prev.filter((id) => id !== versionId);
        if (prev.length < 2) return [...prev, versionId];
        const ids = (versions ?? []).map((v) => v.versionId);
        const newIdx = ids.indexOf(versionId);
        const dist0 = Math.abs(ids.indexOf(prev[0]) - newIdx);
        const dist1 = Math.abs(ids.indexOf(prev[1]) - newIdx);
        // Adjacent to exactly one: slide the window (keep the neighbour)
        if (dist0 === 1 && dist1 !== 1) return [prev[0], versionId];
        if (dist1 === 1 && dist0 !== 1) return [prev[1], versionId];
        // Otherwise: keep the item furthest away to maximise diff spread
        return dist0 >= dist1 ? [prev[0], versionId] : [prev[1], versionId];
      });
    },
    [versions]
  );

  let olderVersionId = null;
  let newerVersionId = null;
  if (selectedIds.length === 2) {
    const idx0 = (versions ?? []).findIndex(
      (v) => v.versionId === selectedIds[0]
    );
    const idx1 = (versions ?? []).findIndex(
      (v) => v.versionId === selectedIds[1]
    );
    if (idx0 > idx1) {
      olderVersionId = selectedIds[0];
      newerVersionId = selectedIds[1];
    } else {
      olderVersionId = selectedIds[1];
      newerVersionId = selectedIds[0];
    }
  } else if (selectedIds.length === 1) {
    newerVersionId = selectedIds[0];
  }

  const {
    currentData: newerData,
    isLoading: newerLoading,
  } = useGetStaticDataVersionQuery(
    { companyId, key: staticDataKey, versionId: newerVersionId },
    { skip: !newerVersionId }
  );
  const {
    currentData: olderData,
    isLoading: olderLoading,
  } = useGetStaticDataVersionQuery(
    { companyId, key: staticDataKey, versionId: olderVersionId },
    { skip: !olderVersionId }
  );

  const contentLoading = newerLoading || olderLoading;
  const showDiff = selectedIds.length === 2;
  const showContent = selectedIds.length === 1;

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={false}
      PaperProps={{
        style: {
          width: "90vw",
          height: "90vh",
          maxWidth: "90vw",
          maxHeight: "90vh",
          borderRadius: 12,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant={"h6"}>{title}</Typography>
          <IconButton icon={CloseIcon} tooltip={"Close"} onClick={close} />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ height: "calc(90vh - 130px)" }}>
          {versionsLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <DefaultLoader size={48} />
            </Box>
          ) : versionsError ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 2,
              }}
            >
              <Alert severity={"error"}>
                {"Failed to load version history."}
              </Alert>
              <CustomButton
                variant={"contained"}
                color={"primary"}
                onClick={() => refetchVersions()}
              >
                {"Retry"}
              </CustomButton>
            </Box>
          ) : (
            <Stack direction={"row"} spacing={2} sx={{ height: "100%" }}>
              <Stack
                width={280}
                spacing={0.75}
                flexShrink={0}
                sx={{ overflowY: "auto" }}
              >
                {(versions ?? []).map((version, index) => {
                  const isSelected = selectedIds.includes(version.versionId);
                  const number = (versions ?? []).length - index;
                  return (
                    <AuditLogTooltip key={version.versionId} log={version.log}>
                      <Button
                        variant={isSelected ? "contained" : null}
                        disableElevation
                        color={"primary"}
                        sx={{
                          color: isSelected ? null : "black",
                          textTransform: "unset",
                        }}
                        onClick={() => toggleVersion(version.versionId)}
                      >
                        <Stack width={"100%"}>
                          <Typography variant={"subtitle2"} textAlign={"left"}>
                            {"Version "}
                            {number}
                          </Typography>
                          <Typography variant={"caption"} textAlign={"left"}>
                            {readableDate(version.lastModified, {
                              includeTime: true,
                            })}
                          </Typography>
                        </Stack>
                      </Button>
                    </AuditLogTooltip>
                  );
                })}
              </Stack>
              <Box sx={{ flex: 1, height: "100%", overflow: "hidden" }}>
                {contentLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <DefaultLoader size={48} />
                  </Box>
                ) : showDiff ? (
                  <DiffEditor
                    original={JSON.stringify(olderData, null, 2)}
                    modified={JSON.stringify(newerData, null, 2)}
                    language={"json"}
                    height={EDITOR_HEIGHT}
                  />
                ) : showContent ? (
                  <CodeEditor
                    value={JSON.stringify(newerData, null, 2)}
                    language={"json"}
                    readOnly
                    height={EDITOR_HEIGHT}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <Typography color={"text.secondary"}>
                      {"Select one version to view, or two to compare"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConfigHistoryModal;
